import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader, AdminTable } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Condition {
  id: number;
  name: string;
  simpleName: string;
  conditionType: string;
  nurseDescription: string;
  patientDescription: string;
  prevalence: string | null;
  keySymptoms: string[];
  relatedMedications: string[];
  niceGuideline: string | null;
  aetiopathogenesis: string | null;
  managementMedical: string | null;
  managementNursing: string | null;
  sources: string | null;
  pathology: string | null;
  imaging: string | null;
  prognosis: string | null;
  otPtInterventions: string | null;
  charityLinks: { name: string; url: string; description: string }[] | null;
  lastReviewedAt: string | null;
  reviewedBy: string | null;
  reviewCycleMonths: number | null;
  createdAt: string;
}

const EMPTY: Omit<Condition, "id" | "createdAt"> = {
  name: "",
  simpleName: "",
  conditionType: "inflammatory",
  nurseDescription: "",
  patientDescription: "",
  prevalence: "",
  keySymptoms: [],
  relatedMedications: [],
  niceGuideline: "",
  aetiopathogenesis: "",
  managementMedical: "",
  managementNursing: "",
  sources: "",
  pathology: "",
  imaging: "",
  prognosis: "",
  otPtInterventions: "",
  charityLinks: [],
  lastReviewedAt: "",
  reviewedBy: "",
  reviewCycleMonths: 12,
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? "Request failed");
  }
  return res.json();
}

function ConditionForm({
  value,
  onChange,
}: {
  value: Omit<Condition, "id" | "createdAt">;
  onChange: (v: Omit<Condition, "id" | "createdAt">) => void;
}) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  const arrStr = (a: string[]) => a.join(", ");
  const strArr = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Name *</Label>
          <Input value={value.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Rheumatoid Arthritis" />
        </div>
        <div>
          <Label>Simple name *</Label>
          <Input value={value.simpleName} onChange={(e) => set("simpleName", e.target.value)} placeholder="e.g. RA" />
        </div>
      </div>
      <div>
        <Label>Condition type *</Label>
        <Input value={value.conditionType} onChange={(e) => set("conditionType", e.target.value)} placeholder="e.g. inflammatory" />
      </div>
      <div>
        <Label>Nurse description *</Label>
        <Textarea rows={3} value={value.nurseDescription} onChange={(e) => set("nurseDescription", e.target.value)} />
      </div>
      <div>
        <Label>Patient description *</Label>
        <Textarea rows={3} value={value.patientDescription} onChange={(e) => set("patientDescription", e.target.value)} />
      </div>
      <div>
        <Label>Prevalence</Label>
        <Input value={value.prevalence ?? ""} onChange={(e) => set("prevalence", e.target.value)} />
      </div>
      <div>
        <Label>Key symptoms (comma-separated)</Label>
        <Input value={arrStr(value.keySymptoms)} onChange={(e) => set("keySymptoms", strArr(e.target.value))} placeholder="Joint pain, swelling, fatigue" />
      </div>
      <div>
        <Label>Related medications (comma-separated)</Label>
        <Input value={arrStr(value.relatedMedications)} onChange={(e) => set("relatedMedications", strArr(e.target.value))} />
      </div>
      <div>
        <Label>NICE guideline reference</Label>
        <Input value={value.niceGuideline ?? ""} onChange={(e) => set("niceGuideline", e.target.value)} />
      </div>
      <div>
        <Label>Aetiopathogenesis</Label>
        <Textarea rows={2} value={value.aetiopathogenesis ?? ""} onChange={(e) => set("aetiopathogenesis", e.target.value)} />
      </div>
      <div>
        <Label>Medical management</Label>
        <Textarea rows={2} value={value.managementMedical ?? ""} onChange={(e) => set("managementMedical", e.target.value)} />
      </div>
      <div>
        <Label>Nursing management</Label>
        <Textarea rows={2} value={value.managementNursing ?? ""} onChange={(e) => set("managementNursing", e.target.value)} />
      </div>
      <div className="border-t border-slate-200 pt-3 mt-2">
        <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Clinical Domains</p>
      </div>
      <div>
        <Label>Pathology</Label>
        <Textarea rows={2} value={value.pathology ?? ""} onChange={(e) => set("pathology", e.target.value)} />
      </div>
      <div>
        <Label>Imaging</Label>
        <Textarea rows={2} value={value.imaging ?? ""} onChange={(e) => set("imaging", e.target.value)} />
      </div>
      <div>
        <Label>Prognosis</Label>
        <Textarea rows={2} value={value.prognosis ?? ""} onChange={(e) => set("prognosis", e.target.value)} />
      </div>
      <div>
        <Label>OT / PT Interventions</Label>
        <Textarea rows={2} value={value.otPtInterventions ?? ""} onChange={(e) => set("otPtInterventions", e.target.value)} />
      </div>
      <div className="border-t border-slate-200 pt-3 mt-2">
        <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Sources & Review</p>
      </div>
      <div>
        <Label>Sources / References</Label>
        <Textarea rows={2} value={value.sources ?? ""} onChange={(e) => set("sources", e.target.value)} placeholder="e.g. NICE NG100 (2018, updated 2020). BSR Guidelines (2021)." />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Reviewed by</Label>
          <Input value={value.reviewedBy ?? ""} onChange={(e) => set("reviewedBy", e.target.value)} placeholder="e.g. RheumHub Clinical Editorial Board" />
        </div>
        <div>
          <Label>Review cycle (months)</Label>
          <Input type="number" value={value.reviewCycleMonths ?? 12} onChange={(e) => set("reviewCycleMonths", parseInt(e.target.value) || 12)} />
        </div>
      </div>
    </div>
  );
}

export default function AdminConditions() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<Condition | null>(null);
  const [form, setForm] = useState<Omit<Condition, "id" | "createdAt">>(EMPTY);

  const { data: conditions = [], isLoading } = useQuery<Condition[]>({
    queryKey: ["admin", "conditions"],
    queryFn: () => apiFetch("/api/admin/content/conditions"),
  });

  const saveMutation = useMutation({
    mutationFn: (body: Omit<Condition, "id" | "createdAt">) =>
      editing
        ? apiFetch(`/api/admin/content/conditions/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        : apiFetch("/api/admin/content/conditions", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "conditions"] });
      setDialogOpen(false);
      toast({ title: editing ? "Condition updated" : "Condition created" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/content/conditions/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "conditions"] });
      toast({ title: "Condition deleted" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  }

  function openEdit(c: Condition) {
    setEditing(c);
    setForm({
      name: c.name,
      simpleName: c.simpleName,
      conditionType: c.conditionType,
      nurseDescription: c.nurseDescription,
      patientDescription: c.patientDescription,
      prevalence: c.prevalence,
      keySymptoms: c.keySymptoms,
      relatedMedications: c.relatedMedications,
      niceGuideline: c.niceGuideline,
      aetiopathogenesis: c.aetiopathogenesis,
      managementMedical: c.managementMedical,
      managementNursing: c.managementNursing,
      sources: c.sources,
      pathology: c.pathology,
      imaging: c.imaging,
      prognosis: c.prognosis,
      otPtInterventions: c.otPtInterventions,
      charityLinks: c.charityLinks,
      lastReviewedAt: c.lastReviewedAt,
      reviewedBy: c.reviewedBy,
      reviewCycleMonths: c.reviewCycleMonths,
    });
    setDialogOpen(true);
  }

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Conditions"
        description={`${conditions.length} condition${conditions.length !== 1 ? "s" : ""}`}
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New condition
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <AdminTable
          columns={["Name", "Type", "Symptoms", "Actions"]}
          rows={conditions.map((c) => [
            <div key="name">
              <p className="font-medium text-slate-900">{c.name}</p>
              <p className="text-xs text-slate-500">{c.simpleName}</p>
            </div>,
            <Badge key="type" variant="outline" className="text-xs">{c.conditionType}</Badge>,
            <span key="symp" className="text-xs text-slate-500">{c.keySymptoms.slice(0, 2).join(", ")}{c.keySymptoms.length > 2 ? "…" : ""}</span>,
            <div key="actions" className="flex items-center gap-2">
              <Button size="icon" variant="ghost" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteId(c.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>,
          ])}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit condition" : "New condition"}</DialogTitle>
          </DialogHeader>
          <ConditionForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete condition?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the condition and cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
